import { CommonModule, DecimalPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { combineLatest, distinctUntilChanged, debounceTime, filter, take } from 'rxjs';
import { SmallNumberPipe } from 'src/app/pipes/small-number/small-number.pipe';
import { AfmBeService } from 'src/app/services/afm-be/afm-be.service';
import { AlbionImageService } from 'src/app/services/albion-image/albion-image.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { AuthService } from 'src/app/services/auth-service/auth-service.service';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { TopItemsService } from 'src/app/services/top-items/top-items.service';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { CheckPriceButtonComponent } from 'src/app/shared/components/check-price-button/check-price-button.component';
import { CopyTextButtonComponent } from 'src/app/shared/components/copy-text-button/copy-text-button.component';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { LOCATIONS } from 'src/app/shared/constants/locations.constant';
import { SALES_TAXES } from 'src/app/shared/constants/market.constant';
import { getServerName } from 'src/app/shared/constants/servers.constant';
import { SUBS_LIMITS } from 'src/app/shared/constants/subs.constants';
import { MarketResponse } from 'src/app/shared/models/ao-data-interfaces';
import { MarketFlipRecord, UserFlipsResponse } from 'src/app/shared/models/flipper.model';

@Component({
  selector: 'app-your-flips',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatButtonModule,
    MatIconModule,
    CopyTextButtonComponent,
    CheckPriceButtonComponent,
    SmallNumberPipe,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    MatTooltip,
    MatCheckboxModule,
  ],
  templateUrl: './your-flips.component.html',
  styleUrl: './your-flips.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class YourFlipsComponent implements OnInit {
  SUB_LIMITS = SUBS_LIMITS;
  SALES_TAX = SALES_TAXES.premium.value;
  userFlipsResponse = signal<UserFlipsResponse | null>(null);
  enableDeleteButton = signal<boolean>(true);
  enableUpdateFailFlipButton = signal<boolean>(true);
  displayedColumns: string[] = [
    'itemUsCompleteName',
    'server',
    'flipTime',
    'sellOrderUnitPriceSilver',
    'buyOrderUnitPriceSilver',
    'upgradeCost',
    'profitPremium',
    'profitPercentagePremium',
    'failed',
    'delete',
  ];
  @ViewChild('flipsSort') flipsSort!: MatSort;
  @ViewChild('flipsPaginator') flipsPaginator!: MatPaginator;
  flipsDataSource: MatTableDataSource<MarketFlipRecord> = new MatTableDataSource<MarketFlipRecord>(this.userFlipsResponse()?.flips ?? []);
  marketPrices = signal<{ [server: string]: MarketResponse[] }>({});
  failedFlipsUniqueNames = computed(() => {
    const flips = this.userFlipsResponse()?.flips ?? [];
    const dictionary: { [server: string]: Set<string> } = {};

    flips
      .filter((flip) => flip.failedFlip && flip.soldItem && flip.server)
      .forEach((flip) => {
        dictionary[flip.server] ??= new Set<string>();
        dictionary[flip.server].add(flip.soldItem!.uniqueName);
      });

    return Object.fromEntries(Object.entries(dictionary).map(([server, s]) => [server, [...s]]));
  });

  filterFormGroup = new FormGroup({
    filterInput: new FormControl<string>(''),
    onlyFailed: new FormControl<boolean>(false),
  });

  destroyRef = inject(DestroyRef);
  // MARK: Constructor
  constructor(
    public userSettingsService: UserSettingsService,
    public authService: AuthService,
    public jsonReaderService: JsonReaderService,
    public aoDataGetter: AoDataGetterService,
    public topItemsService: TopItemsService,
    public imageService: AlbionImageService,
    public afmBeService: AfmBeService,
    private snackBar: MatSnackBar,
    private matDialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    effect(() => {
      const failedFlips = this.failedFlipsUniqueNames();
      if (Object.keys(failedFlips).length > 0) {
        Object.entries(failedFlips).forEach(([server, uniqueNames]) => {
          this.aoDataGetter.getPrices(uniqueNames, undefined, undefined, server, undefined).subscribe((response) => {
            this.marketPrices.set({
              ...this.marketPrices(),
              [server]: response,
            });
          });
        });
      }
    });
  }

  // MARK: Lifecycle Hooks
  ngOnInit() {
    this.jsonReaderService.ensureLoaded();
    combineLatest([
      this.authService.user$.pipe(distinctUntilChanged()),
      this.jsonReaderService.hasLoaded$.pipe(distinctUntilChanged()),
      this.userSettingsService.userServer$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        debounceTime(1000),
        filter(([user, hasLoaded, server]) => user != null && hasLoaded === true && server != null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async ([user, hasLoaded, server]) => {
        this.topItemsService
          .getUserFlips(5000)
          .pipe(take(1))
          .subscribe((response) => {
            this.userFlipsResponse.set(response);
            this.setupFlipsDataSource();
          });
      });

    this.filterFormGroup.valueChanges.pipe(debounceTime(250)).subscribe((value) => {
      this.applyFilter(value.filterInput);
    });
  }

  // MARK: Methods
  setupFlipsDataSource() {
    this.flipsDataSource = new MatTableDataSource<MarketFlipRecord>(this.userFlipsResponse()?.flips ?? []);
    if (this.flipsSort) {
      this.flipsSort.active = this.flipsSort.active ? this.flipsSort.active : 'flipTime';
      this.flipsSort.direction = this.flipsSort.direction ? this.flipsSort.direction : 'desc';
      this.flipsDataSource.sort = this.flipsSort;
      this.flipsDataSource.sort.sortChange.emit(this.flipsDataSource.sort);
    }
    this.flipsDataSource.sortingDataAccessor = (flip: MarketFlipRecord, property) => {
      switch (property) {
        case 'itemUsCompleteName':
          return flip.soldItem?.localizedName ?? flip.soldItem?.uniqueName ?? '';
        case 'server':
          return flip.server;
        case 'flipTime':
          return flip.flipTime.getTime();
        case 'buyOrderUnitPriceSilver':
          return flip.sellPrice;
        case 'sellOrderUnitPriceSilver':
          return flip.buyPrice;
        case 'upgradeCost':
          return flip.upgradeCost;
        case 'profitPremium':
          return flip.profitPremium;
        case 'profitPercentagePremium':
          return flip.profitPercentagePremium;
        case 'failed':
          return flip.failedFlip.toString();
        default:
          return flip.profitPremium;
      }
    };
    this.flipsDataSource.paginator = this.flipsPaginator;

    this.flipsDataSource.filterPredicate = (data: MarketFlipRecord, filter: string) => {
      let nameFilter = true;
      if (this.filterFormGroup.controls.filterInput.value) {
        nameFilter = data.soldItem
          ? data.soldItem.localizedName.toLowerCase().includes(this.filterFormGroup.controls.filterInput.value.trim().toLowerCase())
          : false;
      }
      const failedFilter = this.filterFormGroup.controls.onlyFailed.value ? data.failedFlip : true;
      return nameFilter && failedFilter;
    };
    this.cdr.markForCheck();
  }
  deleteFlip(flip: MarketFlipRecord) {
    const dialogRef = this.matDialog.open(DialogComponent, {
      width: '250px',
      data: { title: 'Confirm Flip Deletion', message: `Are you sure you want to delete this flip? It'll be forever lost!` },
    });

    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.enableDeleteButton.set(false);
        this.cdr.markForCheck();

        this.topItemsService.deleteUserFlip(flip.id).subscribe((response) => {
          if (response) {
            this.snackBar.open(`Flip deleted successfully!`, undefined, { duration: 2000 });

            const index = this.userFlipsResponse()?.flips.indexOf(flip);
            if (index !== undefined && index > -1) {
              this.userFlipsResponse()?.flips.splice(index, 1);
            }
            this.setupFlipsDataSource();
          } else {
            this.snackBar.open(`Error deleting flip!`, undefined, { duration: 2000 });
          }
        });

        setTimeout(() => {
          this.enableDeleteButton.set(true);
          this.cdr.markForCheck();
        }, 1000);
      }
    });
  }

  updateFlipFailed(flip: MarketFlipRecord) {
    this.enableUpdateFailFlipButton.set(false);
    this.cdr.markForCheck();

    this.topItemsService.updateFlipFailed(flip.id, !flip.failedFlip).subscribe((response) => {
      if (response) {
        this.snackBar.open(`Flip updated successfully!`, undefined, { duration: 2000 });
        this.flipsDataSource.data[this.flipsDataSource.data.indexOf(flip)].failedFlip = response.failedFlip;
        if (response.failedFlip) {
          if (flip.soldItem) {
            this.aoDataGetter.getPrices([flip.soldItem.uniqueName], undefined, undefined, flip.server, undefined).subscribe((prices) => {
              this.marketPrices.set({
                ...this.marketPrices(),
                [flip.server]: [...(this.marketPrices()[flip.server] || []), ...prices],
              });
            });
          }
        }
        this.flipsDataSource.sort?.sortChange.emit(this.flipsDataSource.sort);
      } else {
        this.snackBar.open(`Error updating flip!`, undefined, { duration: 2000 });
      }
    });

    setTimeout(() => {
      this.enableUpdateFailFlipButton.set(true);
      this.cdr.markForCheck();
    }, 1000);
  }

  applyFilter(value: string | null | undefined) {
    this.flipsDataSource.filter = 'go';
  }

  getServerName(id: string): string {
    return getServerName(id);
  }

  getCurrentBlackMarketPrices(flip: MarketFlipRecord): MarketResponse[] | [] {
    if (!flip.server || !this.marketPrices()[flip.server]) {
      return [];
    }
    let marketPrices = this.marketPrices()[flip.server].filter((price) => price.itemTypeId === flip.soldItem?.uniqueName && price.city === flip.sellLocation);
    if (flip.sellLocation === LOCATIONS.BlackMarket) {
      marketPrices = marketPrices.filter((price) => price.qualityLevel.index <= flip.buyQuality.index);
    } else {
      marketPrices = marketPrices.filter((price) => price.qualityLevel.index === flip.buyQuality.index);
    }
    return marketPrices.sort((a, b) => b.buyPriceMax - a.buyPriceMax);
  }

  getAgeInMinutes(price: MarketResponse): number {
    return (new Date().getTime() - price.buyPriceMaxDate.getTime()) / 60000;
  }

  getAgeText(price: MarketResponse): string {
    const age = this.getAgeInMinutes(price);
    const smallNumberPipe = new SmallNumberPipe(new DecimalPipe('en-US'));
    if (age < 60) {
      return `${smallNumberPipe.transform(age)} mins`;
    } else {
      return `${smallNumberPipe.transform(age / 60)} hours`;
    }
  }
}
